import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import AuthPage from "./pages/AuthPage";
import HomePage from "./pages/HomePage";
import CreatePollPage from "./pages/CreatePollPage";
import MyPollsPage from "./pages/MyPollsPage";
import NotificationsPage from "./pages/NotificationsPage";
import ProfilePage from "./pages/ProfilePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import SavedPollsPage from "./pages/SavedPollsPage";
import NotificationsSettingsPage from "./pages/NotificationsSettingsPage";
import AccountPrivacyPage from "./pages/AccountPrivacyPage";
import ActivityPage from "./pages/ActivityPage";
import ReportPollPage from "./pages/ReportPollPage";
import PublicProfilePage from "./pages/PublicProfilePage";
import FollowersListPage from "./pages/FollowersListPage";
import FollowingListPage from "./pages/FollowingListPage";
import AdminPage from "./pages/AdminPage";
import TrendingPage from "./pages/TrendingPage";
import TrendingTopicPage from "./pages/TrendingTopicPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import CommunityGuidelinesPage from "./pages/CommunityGuidelinesPage";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-sm text-muted-foreground"
        data-testid="auth-loading-state"
      >
        Loading Yopinion...
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  return children;
};

function App() {
  return (
    <div className="App" data-testid="app-root">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster richColors theme="light" />
      </AuthProvider>
    </div>
  );
}

const AppRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <GoogleAuthCallback />;
  }
  return (
    <Routes>
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/"
        element={
          <Layout>
            <HomePage />
          </Layout>
        }
      />
      <Route
        path="/poll/:pollId"
        element={
          <Layout>
            <HomePage />
          </Layout>
        }
      />
      <Route
        path="/create"
        element={
          <ProtectedRoute>
            <Layout>
              <CreatePollPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-polls"
        element={
          <ProtectedRoute>
            <Layout>
              <MyPollsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Layout>
              <NotificationsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-notifications"
        element={
          <ProtectedRoute>
            <Layout>
              <NotificationsSettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/saved-polls"
        element={
          <ProtectedRoute>
            <Layout>
              <SavedPollsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account-privacy"
        element={
          <ProtectedRoute>
            <Layout>
              <AccountPrivacyPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/your-activity"
        element={
          <ProtectedRoute>
            <Layout>
              <ActivityPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/:username"
        element={
          <Layout>
            <ProfilePage />
          </Layout>
        }
      />
      <Route
        path="/user/:username"
        element={
          <Layout>
            <PublicProfilePage />
          </Layout>
        }
      />
      <Route
        path="/user/:username/followers"
        element={
          <Layout>
            <FollowersListPage />
          </Layout>
        }
      />
      <Route
        path="/user/:username/following"
        element={
          <Layout>
            <FollowingListPage />
          </Layout>
        }
      />
      <Route
        path="/admin"
        element={
          <Layout>
            <AdminPage />
          </Layout>
        }
      />
      <Route
        path="/trending"
        element={
          <Layout>
            <TrendingPage />
          </Layout>
        }
      />
      <Route
        path="/trending/:keyword"
        element={
          <Layout>
            <TrendingTopicPage />
          </Layout>
        }
      />
      <Route
        path="/report/:pollId"
        element={
          <Layout>
            <ReportPollPage />
          </Layout>
        }
      />
      <Route
        path="/privacy-policy"
        element={
          <Layout>
            <PrivacyPolicyPage />
          </Layout>
        }
      />
      <Route
        path="/terms"
        element={
          <Layout>
            <TermsPage />
          </Layout>
        }
      />
      <Route
        path="/community-guidelines"
        element={
          <Layout>
            <CommunityGuidelinesPage />
          </Layout>
        }
      />
    </Routes>
  );
};

export default App;
